import { useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { Icon, PlanBadge, tokens } from '@hotjar/hotsauce';
import { useGate } from '@hotjar/allowances';
import type { Site } from '@hotjar/site';
import type { WebhooksSettings } from '@hotjar/integrations/domains/webhooks/api';
import { WebhooksFeature } from '@hotjar/integrations';
import { ErrorBoundary } from '@hotjar/error-handler';
import type { KnownReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';

import { ForwardToWebhooksInput } from './ForwardToWebhooksInput';
import { ForwardOption } from '../ForwardOption';

export interface ForwardToWebhooksProps {
  onChange: (settings: Partial<WebhooksSettings>) => void;
  settings: WebhooksSettings;
  header: React.ReactNode;
  currentSite: Site | undefined;
  upsell: (referrer: KnownReferrer) => void;
  contextOfUse: ProductType;
  switchDisabled?: boolean;
  trackingState?: string;
}

const WebhooksIcon = styled(Icon)`
  margin-right: ${tokens.spacer.x1};
  margin-top: 2px;
  margin-bottom: 6px;
  float: left;
`;

const HeaderText = styled.span`
  margin-right: ${tokens.spacer.x1};
`;

export const ForwardToWebhooks = ({
  onChange,
  settings,
  header,
  currentSite,
  upsell,
  contextOfUse,
  switchDisabled = false,
  trackingState,
}: ForwardToWebhooksProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { hasPermission } = useAuthorization();
  const [isEnabled, setIsEnabled] = useState(settings.enabled);
  const { click: trackClick } = useTrack();

  // Feature gate: site is in the minimal plan (business)
  const featureGate =
    contextOfUse === ProductType.Analytics
      ? WebhooksFeature.GATE_ANALYTICS
      : WebhooksFeature.GATE_FEEDBACK;
  const isMinimalPlan = useGate(featureGate, { id: String(currentSite?.id), type: 'site' });

  // Upsell referrer
  const upsellReferrer =
    contextOfUse === ProductType.Analytics
      ? WebhooksFeature.UPSELL_REFERRER_ANALYTICS
      : WebhooksFeature.UPSELL_REFERRER_FEEDBACK;

  // Permission to enable the integration for survey/feedback
  const hasEnablePermission = hasPermission(
    'site',
    `${currentSite?.id}`,
    WebhooksFeature.PERMISSION_ENABLE,
  );

  const onEnableClick = () => {
    if (!isMinimalPlan) {
      upsell(upsellReferrer);
    } else {
      trackClick('Forward Toggle', 'Forward to Webhooks', {
        state_event_was_triggered_on: trackingState,
      });
    }
  };

  const onEnableChange = (enabled: boolean) => {
    setIsEnabled(enabled);
    onChange({ enabled });
  };

  const getSwitchTooltipContent = () => {
    if (!isMinimalPlan) {
      return t('common:forwarding.webhooks.minimal-plan-tooltip');
    }

    if (!hasEnablePermission && !isEnabled) {
      return t('common:forwarding.webhooks.write-tooltip');
    }

    return '';
  };

  const getDisabledState = () => {
    if (switchDisabled) {
      return true;
    }

    if (!isMinimalPlan) {
      return true;
    }

    if (!hasEnablePermission) {
      return true;
    }

    return false;
  };

  const headerElement = (
    <div>
      <WebhooksIcon icon="webhooks" size="m" />
      <HeaderText>{header}</HeaderText>
      {!isMinimalPlan && (
        <PlanBadge
          kind={WebhooksFeature.FEATURE_PLAN}
          size="s"
          onClick={() => upsell(upsellReferrer)}
        />
      )}
    </div>
  );

  const inputElement = (
    <ForwardToWebhooksInput settings={settings} onChange={onChange} currentSite={currentSite} />
  );

  return (
    <ErrorBoundary squadSentryTag="integrations">
      <ForwardOption
        isEnabled={isEnabled}
        onClick={onEnableClick}
        onChange={onEnableChange}
        header={headerElement}
        input={inputElement}
        switchDisabled={getDisabledState()}
        switchTooltipContent={getSwitchTooltipContent()}
        isFixedWidth={false}
      />
    </ErrorBoundary>
  );
};
